import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../provider/AuthProvider";
import { ROUTE_NAMES } from "../../routes/routeConstants";
import { acaPal, acaFont, AcaCTA, AcaIcon } from "../../components/shared/aca";
import { API_URL } from "../../config/api";

const VerifyEmailPage: React.FC = () => {
  const [code, setCode] = useState<string[]>(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState<"success" | "error" | "info">("info");
  const [countdown, setCountdown] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, updateUser } = useAuth();
  const { email: stateEmail, role: stateRole } = (location.state as { email?: string; role?: string }) || {};

  const email = stateEmail || user?.email || "";
  const role = stateRole || user?.role || "employee";

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    const newCode = [...code];
    for (let i = 0; i < pastedData.length; i++) {
      newCode[i] = pastedData[i];
    }
    setCode(newCode);
    const nextEmptyIndex = newCode.findIndex((c) => !c);
    if (nextEmptyIndex !== -1) {
      inputRefs.current[nextEmptyIndex]?.focus();
    } else {
      inputRefs.current[5]?.focus();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const fullCode = code.join("");
    if (fullCode.length !== 6) {
      setMessage("Please enter all 6 digits");
      setMessageType("error");
      return;
    }
    setLoading(true);
    setMessage("");
    if (!email) {
      setMessage("Email not found. Please try registering again.");
      setMessageType("error");
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(`${API_URL}/auth/verify-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, code: fullCode }),
      });
      const data = await res.json();
      if (res.ok) {
        setMessage("Email verified successfully. Redirecting…");
        setMessageType("success");
        if (user) updateUser({ isEmailVerified: true });
        setTimeout(() => {
          if (role === "employer") navigate(ROUTE_NAMES.PAGES.EMPLOYER_ONBOARDING);
          else navigate(ROUTE_NAMES.PAGES.EMPLOYEE_ONBOARDING);
        }, 2000);
      } else {
        setMessage(data.message || "Verification failed");
        setMessageType("error");
      }
    } catch {
      setMessage("Server error, please try again later.");
      setMessageType("error");
    } finally {
      setLoading(false);
    }
  };

  const resendEmail = async () => {
    if (!email || resending || countdown > 0) return;
    setResending(true);
    setMessage("");
    try {
      const res = await fetch(`${API_URL}/auth/send-verification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email }),
      });
      if (res.ok) {
        setMessage("Verification code sent. Check your email.");
        setMessageType("success");
        setCountdown(60);
      } else {
        const data = await res.json();
        setMessage(data.message || "Failed to resend code");
        setMessageType("error");
      }
    } catch {
      setMessage("Failed to resend verification email");
      setMessageType("error");
    } finally {
      setResending(false);
    }
  };

  const ebrow: React.CSSProperties = {
    fontFamily: acaFont.mono,
    fontSize: 10.5,
    letterSpacing: "0.22em",
    textTransform: "uppercase",
    color: acaPal.inkSoft,
  };

  return (
    <div style={{ background: acaPal.paper, color: acaPal.ink, fontFamily: acaFont.sans, minHeight: "calc(100vh - 120px)" }}>
      <section style={{ borderBottom: `1px solid ${acaPal.rule}` }}>
        <div style={{ maxWidth: 1280, margin: "0 auto", padding: "48px 20px 32px" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(12, 1fr)",
              gap: 16,
              borderBottom: `1px solid ${acaPal.rule}`,
              paddingBottom: 14,
              ...ebrow,
            }}
          >
            <div style={{ gridColumn: "span 4" }}>§ Verify</div>
            <div style={{ gridColumn: "span 4", textAlign: "center" }}>Step 02 / 03</div>
            <div style={{ gridColumn: "span 4", textAlign: "right" }}>Code expires in 10m</div>
          </div>
        </div>
      </section>

      <section>
        <div style={{ maxWidth: 560, margin: "0 auto", padding: "56px 20px 80px" }}>
          <div
            style={{
              ...ebrow,
              color: acaPal.accent,
              marginBottom: 22,
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <span style={{ width: 28, height: 1, background: acaPal.accent }} />
            § Verify your email
          </div>
          <h1
            style={{
              fontFamily: acaFont.sans,
              fontSize: "clamp(40px, 6vw, 64px)",
              lineHeight: 0.95,
              letterSpacing: "-0.035em",
              fontWeight: 600,
              margin: 0,
              color: acaPal.ink,
            }}
          >
            A six-digit code was{" "}
            <span style={{ color: acaPal.accent, fontStyle: "italic", fontFamily: acaFont.serif, fontWeight: 400 }}>
              sent.
            </span>
          </h1>
          <p
            style={{
              fontFamily: acaFont.serif,
              fontSize: 18,
              lineHeight: 1.5,
              color: acaPal.inkSoft,
              margin: "24px 0 0",
            }}
          >
            We sent it to <span style={{ color: acaPal.ink, fontWeight: 600 }}>{email || "your inbox"}</span>. Type or paste it below.
          </p>

          <form onSubmit={handleSubmit} style={{ marginTop: 40 }}>
            <div style={{ display: "flex", gap: 10, justifyContent: "space-between", marginBottom: 32 }}>
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => {
                    inputRefs.current[index] = el;
                  }}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={index === 0 ? handlePaste : undefined}
                  style={{
                    width: 60,
                    height: 72,
                    textAlign: "center",
                    fontFamily: acaFont.sans,
                    fontSize: 28,
                    fontWeight: 600,
                    background: "transparent",
                    border: "none",
                    borderBottom: `2px solid ${digit ? acaPal.ink : acaPal.rule}`,
                    color: acaPal.ink,
                    outline: "none",
                    transition: "border-color 120ms ease",
                  }}
                />
              ))}
            </div>

            <AcaCTA type="submit" kind="ink" size="md" disabled={loading || code.some((d) => !d)}>
              {loading ? "Verifying" : "Verify email"}
            </AcaCTA>
          </form>

          <div
            style={{
              marginTop: 32,
              paddingTop: 18,
              borderTop: `1px solid ${acaPal.rule}`,
              display: "flex",
              alignItems: "center",
              gap: 10,
              ...ebrow,
            }}
          >
            <AcaIcon name="mail" size={14} stroke={acaPal.inkSoft} />
            <span>Didn't receive it?</span>
            {countdown > 0 ? (
              <span style={{ color: acaPal.inkMute }}>Resend in {countdown}s</span>
            ) : (
              <button
                type="button"
                onClick={resendEmail}
                disabled={resending}
                style={{
                  background: "transparent",
                  border: "none",
                  padding: 0,
                  color: acaPal.accent,
                  fontFamily: acaFont.mono,
                  fontSize: 10.5,
                  letterSpacing: "0.22em",
                  textTransform: "uppercase",
                  borderBottom: `1px solid ${acaPal.accent}`,
                  cursor: resending ? "not-allowed" : "pointer",
                }}
              >
                {resending ? "Sending" : "Resend code"}
              </button>
            )}
          </div>

          {message && (
            <div
              style={{
                marginTop: 24,
                padding: "14px 18px",
                borderLeft: `2px solid ${messageType === "error" ? acaPal.accent : acaPal.ink}`,
                background: messageType === "error" ? acaPal.accentSoft : acaPal.card,
                fontFamily: acaFont.sans,
                fontSize: 14,
                color: acaPal.ink,
              }}
            >
              {message}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default VerifyEmailPage;
